// CPI-974: the rules behind the CRM's cancel-booking form — a mandatory category and a mandatory
// written reason, Vietnam only. Why each rule is what it is, and why the helpers are here rather
// than in the modal, are in docs/merge-audit-main-svn-to-master.md, "Vietnam-only notes worth
// keeping (2026-09-06, CPI-974)".

import { ROLE_AGENT } from '../data/roles';

// Equal to CANCELLATION_REASON_MAX_LENGTH in the Showroom's cancel modal.
export const CANCELLATION_REASON_MAX_LENGTH = 500;

export const CANCEL_BOOKING_BANK_FIELDS = [
  'bank_name',
  'bank_account_number',
  'bank_account_name',
];

// Returns error codes, not sentences; the modal resolves each one to a message.
export const validateCancelBookingForm = (form, hasBankFromContact) => {
  const errors = {};
  if (!form.cancellation_type) {
    errors.cancellation_type = 'select_cancellation_type';
  }
  if (!(form.cancellation_reason || '').trim()) {
    errors.cancellation_reason = 'field_required';
  }
  if (!hasBankFromContact) {
    CANCEL_BOOKING_BANK_FIELDS.forEach((key) => {
      if (!(form[key] || '').trim()) {
        errors[key] = 'field_required';
      }
    });
  }
  return errors;
};

// The requestRefund variables: the category travels as sr_cancellation_type, the typed text as
// sr_cancellation_reason.
export const buildRefundVariables = (opportunityId, opportunityData, form) => ({
  opportunity_id: opportunityId,
  sr_cancellation_type: form.cancellation_type,
  sr_cancellation_reason: form.cancellation_reason.trim(),
  sr_status: opportunityData?.sr_status || '',
  customer_id: opportunityData?.customer_id || '',
  bank_name: form.bank_name.trim(),
  bank_account_number: form.bank_account_number.trim(),
  bank_account_name: form.bank_account_name.trim(),
  name: opportunityData?.name || '',
  mobile_country_code: opportunityData?.mobile_country_code || '',
  mobile: opportunityData?.mobile || '',
});

// Cancelling from the CRM is allowed until the booking's unit is reserved (DSM, refining
// CPI-1234 AC 9). CPI-1234 AC 9 first drew the line at sr_status `approved` on the belief that
// approval reserves the unit, but it does not: Showroom only lets a unit be reserved once the
// booking is already approved (mhub-book-api handlers/bookings.go, error_sr_status_not_approved),
// so an approved booking with no reserved unit yet is still cancellable.
//
// Two gates, then. The sr_status window below is every status up to and including `approved`;
// anything past it, and any status this list has not heard of, disables the button, so a new
// sr_status cannot silently reopen cancellation. Separately the page passes whether the unit is
// reserved, read through ViewAvailableUnitsSection.isUnitReserved — the CRM `stage` OR the
// Showroom unit's own status — never through `stage` alone: stage can be edited from the page
// header and moves on to booked/won/lost, so on its own it would reopen reserved bookings.
export const CANCELLABLE_SR_STATUSES = [
  'pending_payment',
  'payment_made',
  'partially_paid_unverified',
  'paid_unverified',
  'paid_verified',
  'approved',
];

export const isCancellableSrStatus = (srStatus) => CANCELLABLE_SR_STATUSES.includes(srStatus);

// Why the Cancel booking button is dim, or null when it is live. Returns reason codes, not
// sentences; the page resolves each one to a tooltip. An in-flight cancellation is reported
// first: it is the more specific fact, and it is the one an agent can act on (wait for the
// approval) rather than a state of the unit.
export const CANCEL_BOOKING_DISABLED_ALREADY_REQUESTED = 'already_requested';
export const CANCEL_BOOKING_DISABLED_UNIT_RESERVED = 'unit_reserved';
export const CANCEL_BOOKING_DISABLED_STATUS = 'status_not_cancellable';
export const CANCEL_BOOKING_DISABLED_NOT_OWNER = 'not_owner';

// An Agent may cancel only their own booking: the one assigned to them or, while nobody is
// assigned, the one they created. Bookings created through the REST path (Showroom / buyer
// flow) stamp created_by with the caller and are only auto-assigned when a round-robin match
// exists (mhub-opportunity functions/apigateway/opportunity/create.go), so an agent's booking
// can reach the CRM unassigned. Once someone is assigned, the assignee alone owns it: a booking
// re-assigned away from its creator (CPI-1213) still opens for the creator, because the backend
// lets an Agent read what they created (functions/appsync/opportunity/access.go), and this is
// what keeps Cancel dim there.
// Every other role is untouched: the check is on ROLE_AGENT alone, not on the L3 level, which
// would also catch ROLE_STAFF. A missing user id never counts as a match.
const UNASSIGNED_VALUES = ['', 'unassigned'];
const isAssigned = (assignedTo) => Boolean(assignedTo) && !UNASSIGNED_VALUES.includes(assignedTo);

export const getBookingOwnerId = ({ assignedTo, createdBy }) => (
  isAssigned(assignedTo) ? assignedTo : (createdBy || '')
);

export const isCancelBookingBlockedForNonOwner = ({
  role, userId, assignedTo, createdBy,
}) => (
  role === ROLE_AGENT
  && !(Boolean(userId) && getBookingOwnerId({ assignedTo, createdBy }) === userId)
);

export const getCancelBookingDisabledReason = ({
  hasExistingRefund,
  srStatus,
  isNonOwnerAgent = false,
  isUnitReserved = false,
}) => {
  if (hasExistingRefund) return CANCEL_BOOKING_DISABLED_ALREADY_REQUESTED;
  if (isNonOwnerAgent) return CANCEL_BOOKING_DISABLED_NOT_OWNER;
  if (isUnitReserved) return CANCEL_BOOKING_DISABLED_UNIT_RESERVED;
  if (!isCancellableSrStatus(srStatus)) return CANCEL_BOOKING_DISABLED_STATUS;
  return null;
};

// The translation key and English fallback for each reason, so a new reason cannot fall
// through to another reason's tooltip.
const CANCEL_BOOKING_TOOLTIPS = {
  [CANCEL_BOOKING_DISABLED_ALREADY_REQUESTED]: [
    'component.label.cancel_booking_already_requested',
    'A cancellation has already been requested for this booking',
  ],
  [CANCEL_BOOKING_DISABLED_UNIT_RESERVED]: [
    'component.label.cancel_booking_unit_reserved',
    'The unit for this booking is reserved, so it can no longer be cancelled here',
  ],
  [CANCEL_BOOKING_DISABLED_STATUS]: [
    'component.label.cancel_booking_status_not_cancellable',
    'This booking can no longer be cancelled at its current status',
  ],
  [CANCEL_BOOKING_DISABLED_NOT_OWNER]: [
    'component.label.cancel_booking_not_owner',
    'Only the agent who owns this booking can cancel it: the assigned agent, or the agent who created it while no agent is assigned',
  ],
};

export const getCancelBookingTooltip = (reason) => CANCEL_BOOKING_TOOLTIPS[reason] || null;
